import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  StyledJournalEntriesTabsInput,
  StyledJournalEntriesTabsSelect,
} from '../pages/NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import ConfidenceBadge from '../ConfidenceBadge';
import {accountingActions} from '../../state/actions/accounting.actions';
import {firebaseAnalytics} from '../../snippets/firebase/analytics';
import {suggestionRecordTypeConstants} from '../../constants/accounting.constants';
import {
  StyledSuggestionFieldRow,
  StyledSuggestionSelectWrapper,
} from './StyledSuggestionTableField';

const dispatchFeedback = (dispatch, record, propName, originalValue, correctedValue) => {
  const recordType = record?._record_type;
  const recordId = recordType === suggestionRecordTypeConstants.VENDOR ? record?.vendor_id : record?.id;
  if (!recordType || !recordId || originalValue == null || correctedValue === originalValue) return;
  dispatch(accountingActions.postSuggestionFeedback({
    record_type: recordType,
    record_id: recordId,
    data: {field_name: propName, original_value: originalValue, corrected_value: correctedValue},
  }));
};

// AI suggestion interactions are standalone events, not flows: logging them as
// flow_started/flow_completed inflates every flow funnel in GA4.
const logSuggestionEvent = (eventName, propName, record, suggestion) => {
  firebaseAnalytics.logEvent(eventName, {
    platform: 'web',
    field: propName,
    record_type: record?._record_type,
    confidence: suggestion?.confidence,
  });
};

const wrapperProps = {
  onClick: (e) => e.stopPropagation(),
};

export const SuggestionTableSelect = ({
  defaultValue,
  filterOption,
  onSelect,
  options,
  placeholder,
  popupMatchSelectWidth,
  propName,
  record,
  showSearch,
  styles,
  suggestion,
  variant,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [userHasEdited, setUserHasEdited] = useState(false);

  useEffect(() => {
    if (suggestion) {
      logSuggestionEvent('ai_suggestion_shown', propName, record, suggestion);
    }
  }, [suggestion?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (value) => {
    if (suggestion && !userHasEdited) {
      if (value !== suggestion?.value) {
        logSuggestionEvent('ai_suggestion_overridden', propName, record, suggestion);
      } else {
        logSuggestionEvent('ai_suggestion_accepted', propName, record, suggestion);
      }
    }
    setUserHasEdited(true);
    dispatchFeedback(dispatch, record, propName, suggestion?.value, value);
    onSelect?.(value);
  };

  return (
    <StyledSuggestionSelectWrapper {...wrapperProps}>
      <StyledSuggestionFieldRow>
        <StyledJournalEntriesTabsSelect
          defaultValue={defaultValue}
          filterOption={filterOption}
          onSelect={handleSelect}
          options={options}
          placeholder={placeholder}
          popupMatchSelectWidth={popupMatchSelectWidth}
          showSearch={showSearch}
          styles={styles}
          variant={variant}
          {...rest}
        />
        {suggestion && !userHasEdited && <ConfidenceBadge suggestion={suggestion} />}
      </StyledSuggestionFieldRow>
    </StyledSuggestionSelectWrapper>
  );
};

export const SuggestionTableInput = ({
  debounceTime,
  defaultValue,
  onChange,
  onImmediatelyChange,
  placeholder,
  propName,
  record,
  suggestion,
  variant,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [userHasEdited, setUserHasEdited] = useState(false);

  const effectiveDefaultValue = defaultValue ?? suggestion?.value;

  useEffect(() => {
    if (suggestion) {
      logSuggestionEvent('ai_suggestion_shown', propName, record, suggestion);
    }
  }, [suggestion?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDebouncedChange = (value) => {
    if (suggestion && !userHasEdited) {
      if (value !== suggestion?.value) {
        logSuggestionEvent('ai_suggestion_overridden', propName, record, suggestion);
      } else {
        logSuggestionEvent('ai_suggestion_accepted', propName, record, suggestion);
      }
    }
    setUserHasEdited(true);
    dispatchFeedback(dispatch, record, propName, suggestion?.value, value);
    onChange?.(value);
  };

  return (
    <StyledSuggestionFieldRow {...wrapperProps}>
      <StyledJournalEntriesTabsInput
        defaultValue={effectiveDefaultValue}
        debounceTime={debounceTime}
        onChange={onImmediatelyChange}
        onDebouncedChange={handleDebouncedChange}
        placeholder={placeholder}
        variant={variant}
        {...rest}
      />
      {suggestion && !userHasEdited && <ConfidenceBadge suggestion={suggestion} />}
    </StyledSuggestionFieldRow>
  );
};
